import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Etiquette, Message } from "@cabinet/charte";
import { ErreurApi, justificatifsApi, type JustificatifVue } from "../api/client";
import { formaterNombre } from "../domaine/formats";
import { useSession } from "../session";

const taille = (octets: number) =>
  octets >= 1_048_576 ? `${(octets / 1_048_576).toFixed(1).replace(".", ",")} Mo` : `${formaterNombre(Math.ceil(octets / 1024))} Ko`;

/**
 * Pièces justificatives d'une écriture (EF-1505) : les ouvrir, en déposer, et
 * — sur un brouillard seulement — en détacher. Une écriture validée garde les
 * siennes : le lien est indissociable.
 */
export function JustificatifsDePiece({ piece, estModifiable }: { piece: string; estModifiable: boolean }) {
  const { peut } = useSession();
  const cache = useQueryClient();
  const [apercu, setApercu] = useState<JustificatifVue | null>(null);
  const [retour, setRetour] = useState<{ type: "succes" | "erreur" | "avertissement"; texte: string } | null>(null);
  const liste = useQuery({ queryKey: ["justificatifs", piece], queryFn: () => justificatifsApi.dePiece(piece) });

  const rafraichir = () => void cache.invalidateQueries({ queryKey: ["justificatifs", piece] });
  const depot = useMutation({
    mutationFn: (fichier: File) => justificatifsApi.deposer(piece, fichier),
    onSuccess: ({ justificatif, dejaDepose }) => {
      rafraichir();
      setRetour(
        dejaDepose
          ? {
              type: "avertissement",
              texte: `Ce fichier était déjà déposé dans le dossier${justificatif.pieces.length > 1 ? `, et justifie ${justificatif.pieces.length} écritures` : ""} : il n'est gardé qu'une fois.`,
            }
          : { type: "succes", texte: `« ${justificatif.nomDeFichier} » déposé.` },
      );
    },
    onError: (echec) => setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "Le dépôt n'a pas abouti." }),
  });
  const detachement = useMutation({
    mutationFn: (justificatif: string) => justificatifsApi.detacher(piece, justificatif),
    onSuccess: rafraichir,
    onError: (echec) => setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "Le détachement n'a pas abouti." }),
  });

  return (
    <div className="justificatifs">
      <h3>
        Pièces justificatives{" "}
        {liste.data != null && liste.data.length === 0 && <Etiquette variante="avertissement">Aucune</Etiquette>}
      </h3>
      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}
      <ul className="liste-de-justificatifs">
        {(liste.data ?? []).map((j) => (
          <li key={j.id}>
            <button type="button" className="tertiaire" onClick={() => setApercu(apercu?.id === j.id ? null : j)}>
              {j.nomDeFichier}
            </button>{" "}
            <span className="discret-texte">
              {taille(j.taille)} · empreinte <code title={j.empreinte}>{j.empreinte.slice(0, 12)}…</code>
              {j.pieces.length > 1 && ` · justifie ${j.pieces.length} écritures`}
            </span>{" "}
            <a href={justificatifsApi.contenu(j.id)} target="_blank" rel="noreferrer" className="discret-texte">
              Ouvrir
            </a>
            {estModifiable && peut("SAISIR_ECRITURE") && (
              <button
                type="button"
                className="tertiaire"
                onClick={() => window.confirm(`Détacher « ${j.nomDeFichier} » de ce brouillard ?`) && detachement.mutate(j.id)}
              >
                Détacher
              </button>
            )}
          </li>
        ))}
      </ul>
      {apercu != null &&
        (apercu.typeDeMedia === "application/pdf" ? (
          <iframe className="apercu-justificatif" src={justificatifsApi.contenu(apercu.id)} title={apercu.nomDeFichier} />
        ) : (
          <img className="apercu-justificatif" src={justificatifsApi.contenu(apercu.id)} alt={apercu.nomDeFichier} />
        ))}
      {peut("DEPOSER_PIECE") && (
        <label className="depot-de-justificatif discret-texte">
          Déposer un PDF ou une image :{" "}
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            disabled={depot.isPending}
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              setRetour(null);
              if (fichier != null) depot.mutate(fichier);
              e.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );
}
